import { useEffect, useState } from 'react';
import { CalendarDays, ChevronDown, FileText, Globe, Image, Palette } from 'lucide-react';
import Screen from '../demo/Screen';
import { CheckDiscGlyph } from '../mocks/ProductGlyphs';
import {
  BracesGlyph,
  CloudGlyph,
  CodeGlyph,
  FlameGlyph,
  MarkupShieldGlyph,
  MessageFileGlyph,
  PadlockGlyph,
  PdfGlyph,
  ServerGlyph,
  SheetGlyph,
  ShieldSolidGlyph,
  ShieldTickGlyph,
} from '../mocks/TileGlyphs';
import { BuildDiagram, IntegrateDiagram, TestDiagram } from './LiveDiagrams';
import './GettingLive.css';

/*
 * Entrance (once per visit): the three step cards fade and rise in order, then
 * the connectors between them draw. The CSS owns the motion; ENTER_MS is when
 * the class is dropped, a little after the last connector has drawn. Under
 * reduced motion there is no entrance: the final layout shows at once.
 */
const ENTER_MS = 1300;
const REDUCE = '(prefers-reduced-motion: reduce)';
const prefersStill = () => typeof window.matchMedia === 'function' && window.matchMedia(REDUCE).matches;

/* A green check disc, the default checklist mark. */
function Tick() {
  return <CheckDiscGlyph size={20} color="#16955a" />;
}

const STEPS = [
  {
    n: 1,
    title: 'Integrate & Map',
    text: 'We connect with your LIS/HIS, set up API integration and map your parameters.',
    Diagram: IntegrateDiagram,
    checklist: ['API integration', 'Parameter mapping', 'Data validation', 'Test with sample data'],
  },
  {
    n: 2,
    title: 'Build & Configure',
    text: 'We configure your report templates, branding and required settings.',
    Diagram: BuildDiagram,
    checklist: ['Report templates', 'Branding', 'Sections & content', 'Language support'],
    icons: [Image, Palette, FileText, Globe],
  },
  {
    n: 3,
    title: 'Test, Train & Go Live',
    text: 'We run UAT with your team, test with real-world data and provide training.',
    Diagram: TestDiagram,
    checklist: ['UAT with your data', 'End-to-end testing', 'Team training', 'Go-live support'],
  },
];

const FORMATS = [
  { key: 'hl7', label: 'HL7', Glyph: MessageFileGlyph },
  { key: 'fhir', label: 'FHIR', Glyph: FlameGlyph },
  { key: 'json', label: 'JSON', Glyph: BracesGlyph, tone: 'purple' },
  { key: 'pdf', label: 'PDF', Glyph: PdfGlyph },
  { key: 'csv', label: 'CSV', Glyph: SheetGlyph },
  { key: 'xml', label: 'XML', Glyph: CodeGlyph, tone: 'blue' },
  { key: 'html', label: 'HTML', Glyph: MarkupShieldGlyph },
];

/* `bold`: how many of the two lines are set bold, as in the mockup. */
const SECURITY = [
  { key: 'hipaa', lines: ['HIPAA', 'Compliant'], bold: 1, Glyph: ShieldTickGlyph },
  { key: 'dpdp', lines: ['DPDP Act', 'Compliant'], bold: 1, Glyph: ShieldSolidGlyph },
  { key: 'aws', lines: ['AWS', 'Infrastructure'], bold: 2, Glyph: CloudGlyph },
  { key: 'onprem', lines: ['On-premise /', 'Client server'], bold: 0, Glyph: ServerGlyph },
  { key: 'vapt', lines: ['VAPT', 'Tested'], bold: 1, Glyph: PadlockGlyph },
];

/*
 * The drawn arrow from the previous step into this one: across the gap on wide
 * screens, down the gap when the steps stack. Decorative; the ordered list
 * carries the order for assistive tech.
 */
function Connector() {
  return (
    <span className="glive-link" aria-hidden="true">
      <svg className="glive-link__svg glive-link__svg--across" viewBox="0 0 30 16" focusable="false">
        <path className="glive-link__line" d="M3 8H26" pathLength="1" />
        <path className="glive-link__head" d="M21 3.5 26.5 8 21 12.5" pathLength="1" />
      </svg>
      <svg className="glive-link__svg glive-link__svg--down" viewBox="0 0 16 34" focusable="false">
        <path className="glive-link__line" d="M8 3V28" pathLength="1" />
        <path className="glive-link__head" d="M3.5 23 8 28.5 12.5 23" pathLength="1" />
      </svg>
    </span>
  );
}

function StepCard({ step, open, onToggle }) {
  const { n, title, text, Diagram, checklist, icons } = step;
  const buttonId = `glive-step-${n}-button`;
  const panelId = `glive-step-${n}-checklist`;
  return (
    <li className={`card glive-step glive-step--${n}${open ? ' is-open' : ''}`}>
      {n > 1 && <Connector />}
      <span className="glive-step__num" aria-hidden="true">
        {n}
      </span>
      <h2 className="glive-step__head">
        <button
          type="button"
          id={buttonId}
          className="glive-step__toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
          data-trapezium-id={`getting-live.integration.step-${n}`}
        >
          <span className="glive-step__title">{title}</span>
          <ChevronDown className="glive-step__chevron" size={20} aria-hidden="true" />
        </button>
      </h2>
      <p className="glive-step__text">{text}</p>
      <div className="glive-step__visual">
        <Diagram />
        <div className="glive-step__list">
          <div
            id={panelId}
            role="region"
            aria-labelledby={buttonId}
            className="glive-check"
            hidden={!open}
            data-trapezium-state={open ? 'expanded' : 'collapsed'}
          >
            <ul>
              {checklist.map((item, i) => {
                const Icon = icons?.[i];
                return (
                  <li key={item} style={{ '--i': i }}>
                    {Icon ? <Icon className="glive-check__icon" size={18} strokeWidth={1.9} aria-hidden="true" /> : <Tick />}
                    {item}
                  </li>
                );
              })}
            </ul>
          </div>
          {/* Collapsed: the same card shape with placeholder lines, so the layout matches the mockup. */}
          {!open && (
            <div className="glive-check glive-check--folded" aria-hidden="true">
              <ul>
                {checklist.map((item, i) => {
                  const Icon = icons?.[i];
                  return (
                    <li key={item}>
                      {Icon ? <Icon className="glive-check__icon" size={18} strokeWidth={1.9} /> : <Tick />}
                      <i style={{ width: `${46 + ((i * 17) % 30)}%` }} />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

export default function GettingLive() {
  const [openStep, setOpenStep] = useState(1);
  const [entering, setEntering] = useState(() => !prefersStill());

  useEffect(() => {
    if (!entering) return undefined;
    const t = setTimeout(() => setEntering(false), ENTER_MS);
    return () => clearTimeout(t);
  }, [entering]);

  return (
    <Screen id="getting-live.integration" title="Integration" className="glive">
      <div className="glive-head">
        <div>
          <p className="eyebrow glive-eyebrow">Integration &amp; Support</p>
          <h1 className="headline glive-headline">
            <em>A smooth integration.</em> Dedicated support. Minimal effort.
          </h1>
          <p className="lede glive-lede">
            Our onboarding team works with you end-to-end, from integration to go-live.
          </p>
        </div>
        <p className="glive-badge">
          <CalendarDays className="glive-badge__icon" size={64} strokeWidth={1.7} aria-hidden="true" />
          <span>
            <span className="glive-badge__kicker">You are live in</span>{' '}
            <strong className="glive-badge__days">14 days</strong>
          </span>
        </p>
      </div>

      <ol
        className={`glive-steps${entering ? ' is-entering' : ''}`}
        aria-label="Three steps to go live"
        data-trapezium-state={entering ? 'entering' : 'settled'}
      >
        {STEPS.map((step) => (
          <StepCard
            key={step.n}
            step={step}
            open={openStep === step.n}
            onToggle={() => setOpenStep((cur) => (cur === step.n ? null : step.n))}
          />
        ))}
      </ol>

      <div className="glive-panels">
        <section className="card glive-panel" aria-labelledby="glive-integration">
          <p className="eyebrow glive-panel__eyebrow">Integration</p>
          <h2 id="glive-integration" className="glive-panel__title">
            Works with your existing systems.
          </h2>
          <p className="glive-panel__text">We integrate with 100+ LIS and HIS, and any API-ready system.</p>
          <ul className="glive-tiles glive-tiles--formats">
            {FORMATS.map(({ key, label, Glyph, tone }) => (
              <li key={key} className={`glive-tile${tone ? ` glive-tile--${tone}` : ''}`}>
                <Glyph size={32} />
                <b>{label}</b>
              </li>
            ))}
          </ul>
        </section>

        <section className="card glive-panel" aria-labelledby="glive-security">
          <p className="eyebrow glive-panel__eyebrow">Security &amp; compliance</p>
          <h2 id="glive-security" className="glive-panel__title">
            Your data, always secure.
          </h2>
          <p className="glive-panel__text">
            Enterprise-grade security, compliance and flexible hosting options.
          </p>
          <ul className="glive-tiles glive-tiles--security">
            {SECURITY.map(({ key, lines, bold, Glyph }) => (
              <li key={key} className="glive-tile glive-tile--blue">
                <Glyph size={key === 'aws' ? 42 : 36} />
                <span className="glive-tile__label">
                  <span className={bold > 0 ? 'is-bold' : undefined}>{lines[0]}</span>{' '}
                  <span className={bold > 1 ? 'is-bold' : undefined}>{lines[1]}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Screen>
  );
}
